import { gql } from '../gql';
import I18n from 'i18n-js';
import React from 'react';
import { withRouter, WithRouterProps } from 'react-router';
import { isOutletHub } from '../url_helpers';

import { RCListingCard } from '@reverbdotcom/cadence/components';
import { EyebrowTextProps } from '@reverbdotcom/cadence/components/RCText/RCText';

import { SOLD_LISTING_STATES, WEB } from '../constants';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';
import { buildListingClickEvent } from '../elog/mparticle_tracker';
import { WatchParentComponentName } from '../event_tracking/watchlist';
import CreateOfferButton from '../offers/create_offer_button';
import {
  arbiter_Interaction_InteractionType,
  BumpKeyFragment,
  FinancingFieldsFragment,
  InOtherCartsCardDataFragment,
  ListingCardFieldsFragment,
  SellerInfoDataFragment,
} from '../gql/graphql';
import { useUser } from '../user_hooks';
import AddToCartButton from './add_to_cart_button';
import CoreLink from './core_link';
import ImageCarousel from './image_carousel';
import { DisplayStyle, WatchBadge } from './watch_badge';
import { ListingCardMetadata } from './listing_card_metadata';
import { ListingCardReviews } from './reviews_metadata';

import {
  buildListingForBuyer,
  buildPrice,
  getEyebrow,
  getRCListingCardNudges,
  isListingDomesticToBuyer,
  showProximityContent,
  useSoldPriceRecordsQuery,
} from '../listing_helpers';
import ListingCardTag from './listing_card_tag';
import { ListingTitleHtmlTags } from '@reverbdotcom/cadence/components/types';
import { useBumpInteractionTracking } from '../trackBumpInteraction';

const DISPLAY_STYLE = 'squareCard';

type Listing = ListingCardFieldsFragment & FinancingFieldsFragment & InOtherCartsCardDataFragment & BumpKeyFragment & SellerInfoDataFragment;

interface IExternalProps {
  // This type is likely _more_ than what this component needs.
  // Cutting a fragment for this component would help better communicate
  // the contract here.
  listing?: Listing;
  position?: number;
  footerContent?: React.ReactNode;
  hideMetadataAsOffer?: boolean;
  trackingName?: string;
  displayAddToCart?: boolean;
  suggestedListing?: boolean;
  displayMakeAnOffer?: boolean;
  listingsCount?: number;
  combinedShippingDiscount?: boolean;
  trackingQuery?: string;
  trackingFilter?: string;
  trackingSort?: string;
  trackingPage?: number;
  showFlag?: boolean;
  /** Please use a WatchBadgeParentComponent value when possible instead of a plain string! */
  watchBadgeParentComponentName?: WatchParentComponentName | string;
  displayGreatValue?: boolean;
  handleCreateWatchResult?: () => void;
  minimalNudges?: boolean;
  showShippingDisplay?: boolean;
  hideActionsOnMobile?: boolean;
  reviews?: ListingCardReviews;
  includeItemListMetadata?: boolean;
  titleHtmlTag?: keyof typeof ListingTitleHtmlTags;
  cmsComponentId?: string;
  contextHeader?: React.ReactNode;
  showShopInfo?: boolean;
  isRemoveReturnPolicyNudgeExpEnabled?: boolean;
  eyebrow?: {
    text: string;
    color?: EyebrowTextProps['color'];
  };
  enableSwiper?: boolean;
}

type Props = IExternalProps & WithRouterProps;

export function ListingCard({
  listing = undefined,
  location,
  position = undefined,
  footerContent = undefined,
  hideMetadataAsOffer = false,
  trackingName = undefined,
  displayAddToCart = false,
  suggestedListing = false,
  displayMakeAnOffer = false,
  listingsCount = undefined,
  combinedShippingDiscount = false,
  trackingQuery = undefined,
  trackingFilter = undefined,
  trackingSort = undefined,
  trackingPage = undefined,
  showFlag = false,
  watchBadgeParentComponentName = undefined,
  displayGreatValue = true,
  handleCreateWatchResult = undefined,
  minimalNudges = false,
  showShippingDisplay = false,
  hideActionsOnMobile = false,
  reviews = undefined,
  includeItemListMetadata = false,
  titleHtmlTag = 'div',
  cmsComponentId = undefined,
  showShopInfo = false,
  isRemoveReturnPolicyNudgeExpEnabled = false,
  eyebrow = undefined,
  enableSwiper = false,
}: Props) {
  const user = useUser();
  const bumpInteractionTracking = useBumpInteractionTracking({
    interactionType: arbiter_Interaction_InteractionType.CLICK,
    bumpKey: listing?.bumpKey,
    listingId: listing?.id,
  });

  const isSoldListing = SOLD_LISTING_STATES.includes(listing?.state);
  const priceRecordsResult = useSoldPriceRecordsQuery(listing, isSoldListing);

  if (!listing) {
    return <RCListingCard.Skeleton />;
  }

  const soldPrice = priceRecordsResult?.data?.priceRecordsSearch?.priceRecords?.[0]?.amountProduct?.display;
  const decoratedListing = buildListingForBuyer(
    listing,
    { combinedDiscountActive: combinedShippingDiscount },
  );

  const price = buildPrice(decoratedListing, combinedShippingDiscount, showShippingDisplay, isSoldListing, soldPrice);

  function renderMetadata() {
    return (
      <ListingCardMetadata
        listing={listing}
        reviews={reviews}
        hideMetadataAsOffer={hideMetadataAsOffer}
        includeItemListMetadata={includeItemListMetadata}
      />
    );
  }

  function clickEvent() {
    const sellerCountryCode = listing.shop.address?.country?.countryCode;

    const trackedExperiments = [
      // experiment.NAME # Add experiments here
    ];

    return {
      experiments: formatExperimentsForEventAttributes(
        user,
        trackedExperiments,
      ),
      query: trackingQuery,
      filter: trackingFilter,
      sort: trackingSort,
      page: trackingPage,
      ...buildListingClickEvent({
        listing: listing,
        componentName: trackingName,
        cmsComponentId,
        displayStyle: DISPLAY_STYLE,
        position,
        listingsCount,
        city: listing.shop.address?.locality,
        region: listing.shop.address?.region,
        countryCode: sellerCountryCode,
        isListingDomesticToBuyer: isListingDomesticToBuyer(sellerCountryCode, user?.countryCode),
      }),
    };
  }

  function shopLocation() {
    const shopAddress = listing.shop?.address;
    if (!shopAddress) return null;

    const shopAndUserInUs = shopAddress.country?.countryCode === 'US' && user.countryCode === 'US';

    if (showProximityContent(shopAddress, user, location)) {
      if (shopAndUserInUs && shopAddress?.locality && shopAddress.region) {
        return I18n.t('commons.listingCard.location.sameCountryUs', {
          city: shopAddress.locality, state: shopAddress.region,
        });
      }
    }

    return null;
  }

  function shopInfo() {
    return {
      name: listing.shop?.name,
      rating: {
        rating: listing.seller?.feedbackSummary?.rollingRatingPercentage / 100,
        count: listing.seller?.feedbackSummary?.receivedCount,
      },
    };
  }

  const openNewTab = user && user.deviceName === WEB;
  const images = listing.images.map(i => (i.source));
  const nudgeConfig = {
    listing,
    decoratedListing,
    shop: listing.shop,
    displayGreatValue,
    user,
    showFlag,
    location,
    minimalNudges,
    isRemoveReturnPolicyNudgeExpEnabled,
  };

  const eyebrowContent = eyebrow || getEyebrow(listing);

  return (
    <div
      itemProp={includeItemListMetadata ? 'itemListElement' : null}
      itemScope={includeItemListMetadata}
      itemType={includeItemListMetadata ? 'http://schema.org/Product' : null}
      className="height-100 width-100"
    >
      <RCListingCard
        wrapper={(children) => (
          <CoreLink
            to={decoratedListing.listingUrl}
            target={openNewTab ? '_blank' : undefined}
            clickEvent={clickEvent()}
          >
            {children}
          </CoreLink>
        )}
        metaElement={renderMetadata()}
        thumbnailElement={(
          <ImageCarousel
            images={images}
            bumpInteractionTracking={bumpInteractionTracking}
            parentComponentName={trackingName}
            enableSwiper={enableSwiper}
            navigateToUrl={decoratedListing.listingUrl}
            clickEvent={clickEvent()}
            swiperAriaLabel={listing.title}
          />
        )}
        title={listing.title}
        titleHtmlTag={titleHtmlTag}
        conditionLabel={decoratedListing.conditionLabel}
        shopLocation={shopLocation()}
        shopInfo={showShopInfo && shopInfo()}
        price={price}
        nudges={getRCListingCardNudges(nudgeConfig)}
        fullHeight
        eyebrow={eyebrowContent}
        favoriteButtonElement={(
          <WatchBadge
            listing={listing}
            displayStyle={DisplayStyle.ICON_BUTTON}
            iconButtonSize="mini"
            parentComponentName={watchBadgeParentComponentName || trackingName}
            handleCreateWatchResult={handleCreateWatchResult}
            useToast
          />
        )}
        violator={!isOutletHub(location) && <ListingCardTag listing={listing} />}
        hideActionsOnMobile={hideActionsOnMobile}
        primaryAction={displayAddToCart &&
          <AddToCartButton
            listing={listing}
            position={position}
            parentComponentName={trackingName}
            suggestedListing={suggestedListing}
          />
        }
        secondaryAction={displayMakeAnOffer &&
          <CreateOfferButton
            listing={listing}
            parentComponentName={trackingName}
          />
        }
        adminControls={footerContent}
      />
    </div>
  );
}

export const ListingCardFragment = gql(`
  fragment ListingCardFields on Listing {
    _id
    ...ListingForBuyerFields
    ...WatchBadgeData
    ...ListingCreateOfferButtonData
  }
`);

export default withRouter(ListingCard);
